import React, { useState, useRef, useEffect } from 'react';
import { useAudio } from '../contexts/AudioContext';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Quick mute button for game header
 * Click: Toggle mute
 * Long press: Open settings modal
 */
export const AudioMuteButton = ({ style = {} }) => {
  const { settings, toggleMute } = useAudio();
  const [showSettings, setShowSettings] = useState(false);
  const longPressTimer = useRef(null);
  const isLongPress = useRef(false);

  const handleMouseDown = () => {
    isLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      isLongPress.current = true;
      setShowSettings(true);
    }, 500); // 500ms for long press
  };

  const handleMouseUp = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
    // Only toggle mute if it wasn't a long press
    if (!isLongPress.current) {
      toggleMute();
    }
  };

  const handleMouseLeave = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  };

  return (
    <>
      <button
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleMouseDown}
        onTouchEnd={handleMouseUp}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '1.25rem',
          padding: '0.25rem',
          opacity: settings.muted ? 0.5 : 1,
          transition: 'opacity 0.2s',
          ...style,
        }}
        aria-label={settings.muted ? 'Unmute sounds' : 'Mute sounds'}
        title={settings.muted ? 'Sound off (click to unmute, hold for settings)' : 'Sound on (click to mute, hold for settings)'}
      >
        {settings.muted ? '🔇' : '🔊'}
      </button>

      {showSettings && (
        <AudioSettingsModal onClose={() => setShowSettings(false)} />
      )}
    </>
  );
};

/**
 * Full audio settings modal
 */
const AudioSettingsModal = ({ onClose }) => {
  const { t } = useLanguage();
  const { settings, setMasterVolume, toggleCategory, toggleHaptic, toggleMute } = useAudio();
  const [localVolume, setLocalVolume] = useState(settings.masterVolume);

  // Update actual volume with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setMasterVolume(localVolume);
    }, 100);
    return () => clearTimeout(timer);
  }, [localVolume, setMasterVolume]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '1rem',
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '1rem',
          padding: '1.5rem',
          maxWidth: '350px',
          width: '100%',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
        }}>
          <h3 style={{ margin: 0, fontSize: '1.25rem' }}>
            🔊 {t('audio.settings') || 'Audio Settings'}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0.25rem',
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Master Mute */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1rem',
          padding: '0.75rem',
          backgroundColor: settings.muted ? '#fee2e2' : '#dcfce7',
          borderRadius: '0.5rem',
        }}>
          <span style={{ fontWeight: '600' }}>
            {settings.muted ? '🔇 ' : '🔊 '}
            {t('audio.masterSound') || 'Master Sound'}
          </span>
          <button
            onClick={toggleMute}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.25rem',
              border: 'none',
              backgroundColor: settings.muted ? '#ef4444' : '#22c55e',
              color: 'white',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            {settings.muted ? (t('audio.unmute') || 'Unmute') : (t('audio.mute') || 'Mute')}
          </button>
        </div>

        {/* Volume Slider */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: '500',
          }}>
            {t('audio.volume') || 'Volume'}: {Math.round(localVolume * 100)}%
          </label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={localVolume}
            onChange={(e) => setLocalVolume(parseFloat(e.target.value))}
            disabled={settings.muted}
            style={{
              width: '100%',
              cursor: settings.muted ? 'not-allowed' : 'pointer',
              opacity: settings.muted ? 0.5 : 1,
            }}
          />
        </div>

        {/* Category Toggles */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: '#666' }}>
            {t('audio.categories') || 'Sound Categories'}
          </h4>

          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'pointer',
            marginBottom: '0.5rem',
          }}>
            <input
              type="checkbox"
              checked={settings.categories.essential}
              onChange={() => toggleCategory('essential')}
              disabled={settings.muted}
            />
            <span>🎲 {t('audio.essentialSounds') || 'Essential sounds'}</span>
            <span style={{ fontSize: '0.75rem', color: '#999' }}>
              ({t('audio.diceMarks') || 'dice, marks, turns'})
            </span>
          </label>
        </div>

        {/* Haptic Feedback */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={settings.hapticEnabled}
              onChange={toggleHaptic}
            />
            <span>📳 {t('audio.hapticFeedback') || 'Haptic feedback'}</span>
            <span style={{ fontSize: '0.75rem', color: '#999' }}>
              ({t('audio.vibration') || 'vibration'})
            </span>
          </label>
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '0.75rem',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            fontWeight: '600',
            cursor: 'pointer',
            marginTop: '0.5rem',
          }}
        >
          {t('common.close') || 'Close'}
        </button>
      </div>
    </div>
  );
};

export default AudioSettingsModal;
